import useMultiRewardsQuery from '@/composables/queries/useMultiRewardsQuery';
import symbolKeys from '@/constants/symbol.keys';
import { GetRewardsQuery } from '@/services/burrbear-api/graphql/generated/api-types';
import { isSameAddress, Token } from '@xclabs/balancer-sdk';
import { InjectionKey, provide } from 'vue';
import { safeInject } from '../inject';
export interface RewardVaultInfo {
  rewardVaultAddress: `0x${string}`;
  apr: number;
}

export type PoolApr = GetRewardsQuery['rewards'][number]['lps'][number]['apr'];

interface PoolReward {
  rewardContract: `0x${string}`;
  rewardToken: Token;
  pid?: number;
  allocPoint?: number;
}

interface PoolRewards {
  poolAddress: string;
  rewards: PoolReward[];
}

interface PendingPoint {
  pointToken?: string | null;
  amount?: string | null;
}

interface UserStake {
  poolAddress: string;
  reward: PoolReward;
  stakedAmount: string;
  pendingRewards: string;
  pendingPoints: Array<PendingPoint | null>;
}

const poolRewardsProvider = () => {
  const multiRewardsQuery = useMultiRewardsQuery();

  const getPoolRewardVaultInfo = (
    poolAddress: `0x${string}`
  ): RewardVaultInfo | undefined => {
    for (const reward of multiRewardsQuery.data.value?.rewards ?? []) {
      for (const lp of reward.lps) {
        if (isSameAddress(lp.token.address, poolAddress)) {
          const bgtComponent = lp.apr?.components.find(
            c => c.type === 'BASE_REWARDS' && !!c.rewardVaultAddress
          );
          if (bgtComponent) {
            return {
              rewardVaultAddress:
                bgtComponent.rewardVaultAddress as `0x${string}`,
              apr: Number(bgtComponent.value),
            };
          }
        }
      }
    }

    return undefined;
  };

  const getPoolApr = (poolAddress: `0x${string}`) => {
    for (const reward of multiRewardsQuery.data.value?.rewards ?? []) {
      const lp = reward.lps.find(l =>
        isSameAddress(l.token.address, poolAddress)
      );
      if (lp?.apr) return lp.apr;
    }
    return undefined;
  };

  const _allPoolRewards = computed(() => {
    const allPoolRewards: PoolRewards[] = [];
    for (const reward of multiRewardsQuery.data.value?.rewards ?? []) {
      for (const lp of reward.lps) {
        const poolRewards: PoolRewards = {
          poolAddress: lp.token.address,
          rewards: [
            {
              rewardContract: reward.rewardsContract as `0x${string}`,
              rewardToken: reward.rewardToken as Token,
              pid: lp.poolId,
              allocPoint: Number(lp.allocPoint),
            },
          ],
        };

        const existingIndex = allPoolRewards.findIndex(r =>
          isSameAddress(r.poolAddress, poolRewards.poolAddress)
        );
        if (existingIndex >= 0) {
          allPoolRewards[existingIndex].rewards.push(...poolRewards.rewards);
        } else {
          allPoolRewards.push(poolRewards);
        }
      }
    }
    return allPoolRewards;
  });

  const getPoolRewards = (poolAddress: `0x${string}`) => {
    return _allPoolRewards.value.find(r =>
      isSameAddress(r.poolAddress, poolAddress)
    );
  };

  const _userStakes = computed(() => {
    const userStakes: UserStake[] = [];
    for (const rewardContract of multiRewardsQuery.data.value?.user
      ?.rewardContracts ?? []) {
      for (const userInfo of rewardContract.userInfo) {
        _allPoolRewards.value.forEach(poolRewards => {
          const match = poolRewards.rewards.find(
            r =>
              isSameAddress(r.rewardContract, rewardContract.rewardContract) &&
              r.pid === userInfo.poolId
          );
          if (match) {
            userStakes.push({
              poolAddress: poolRewards.poolAddress,
              reward: match,
              stakedAmount: userInfo.stakedAmount,
              pendingRewards: userInfo.pendingRewards ?? '0',
              pendingPoints: userInfo.pendingPoints ?? [],
            });
          }
        });
      }
    }

    return userStakes;
  });

  const getUserStake = (poolAddress: `0x${string}`) => {
    return _userStakes.value.find(stake =>
      isSameAddress(stake.poolAddress, poolAddress)
    );
  };

  return {
    getPoolRewardVaultInfo,
    multiRewardsQuery,
    getPoolApr,
    isPoolAprDataFetched: computed(
      () => multiRewardsQuery.status.value === 'success'
    ),
    getPoolRewards,
    getUserStake,
    userStakes: _userStakes,
  };
};

/**
 * Provider setup: response type + symbol.
 */
export type PoolRewardsProviderResponse = ReturnType<
  typeof poolRewardsProvider
>;
export const PoolRewardsProviderSymbol: InjectionKey<PoolRewardsProviderResponse> =
  Symbol(symbolKeys.Providers.PoolRewards);

export function providePoolRewards(): PoolRewardsProviderResponse {
  const _provider = poolRewardsProvider();
  provide(PoolRewardsProviderSymbol, _provider);
  return _provider;
}

export function usePoolRewards(): PoolRewardsProviderResponse {
  return safeInject(PoolRewardsProviderSymbol);
}
